import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Merge as MergeIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Home as BoothIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import api from '../../services/api';

const DuplicateUserManager = () => {
  const [duplicateGroups, setDuplicateGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mergeDialog, setMergeDialog] = useState(null);
  const [selectedKeepUser, setSelectedKeepUser] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadDuplicateUsers();
  }, []);

  const loadDuplicateUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/duplicate-users');
      setDuplicateGroups(response.data.data);
    } catch (err) {
      setError('Failed to load duplicate users: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleMergeClick = (email, users) => {
    // Find the oldest user as default selection
    const oldestUser = users.reduce((oldest, current) => 
      new Date(current.user_created) < new Date(oldest.user_created) ? current : oldest
    );
    
    setSelectedKeepUser(oldestUser.user_id.toString());
    setMergeDialog({ email, users });
  };

  const handleMergeConfirm = async () => {
    if (!mergeDialog || !selectedKeepUser) return;
    
    try {
      setProcessing(true);
      const keepUserId = parseInt(selectedKeepUser);
      const deleteUserIds = mergeDialog.users
        .filter(user => user.user_id !== keepUserId)
        .map(user => user.user_id);
      
      await api.post('/admin/duplicate-users/merge', {
        keepUserId,
        deleteUserIds,
        normalizedEmail: mergeDialog.email
      });
      
      setSuccess(`Successfully merged duplicate accounts for ${mergeDialog.email}`);
      setMergeDialog(null);
      setSelectedKeepUser('');
      await loadDuplicateUsers();
    } catch (err) {
      setError('Failed to merge users: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user ${userId} (${email}) and all related data?`)) {
      return;
    }
    
    try {
      setProcessing(true);
      await api.delete(`/admin/duplicate-users/${userId}`);
      setSuccess(`Successfully deleted user ${userId}`);
      await loadDuplicateUsers();
    } catch (err) {
      setError('Failed to delete user: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDataSummary = (user) => {
    const items = [];
    if (user.has_vendor) items.push('Vendor');
    if (user.has_booth) items.push('Booth');
    if (user.has_payment) items.push('Payment');
    return items.join(', ') || 'No data';
  };

  const getUserPriority = (user) => {
    let score = 0;
    if (user.has_vendor) score += 3;
    if (user.has_booth) score += 2;
    if (user.has_payment) score += 4;
    return score;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const emailGroups = Object.keys(duplicateGroups);
  
  if (emailGroups.length === 0) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Duplicate User Manager
        </Typography>
        <Alert severity="success">
          No duplicate users found! All email addresses are unique.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Duplicate User Manager
      </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Found {emailGroups.length} email(s) with duplicate accounts. 
        Review each group and choose which account to keep.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {emailGroups.map(email => {
        const users = duplicateGroups[email];
        // Group users by their data to avoid duplicates in display
        const uniqueUsers = users.reduce((acc, user) => {
          const existing = acc.find(u => u.user_id === user.user_id);
          if (!existing) {
            acc.push(user);
          }
          return acc;
        }, []);

        return (
          <Card key={email} sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {email} ({uniqueUsers.length} accounts)
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<MergeIcon />}
                  onClick={() => handleMergeClick(email, uniqueUsers)}
                  disabled={processing}
                >
                  Merge Accounts
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Email (Original Case)</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Business</TableCell>
                      <TableCell>Booth</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uniqueUsers
                      .sort((a, b) => new Date(a.user_created) - new Date(b.user_created))
                      .map(user => (
                      <TableRow key={user.user_id}>
                        <TableCell>{user.user_id}</TableCell>
                        <TableCell>{user.actual_email}</TableCell>
                        <TableCell>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{formatDate(user.user_created)}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            {user.has_vendor && (
                              <Tooltip title="Has vendor data">
                                <Chip icon={<BusinessIcon />} label="V" size="small" color="primary" />
                              </Tooltip>
                            )}
                            {user.has_booth && (
                              <Tooltip title="Has booth assignment">
                                <Chip icon={<BoothIcon />} label="B" size="small" color="secondary" />
                              </Tooltip>
                            )}
                            {user.has_payment && (
                              <Tooltip title="Has payment records">
                                <Chip icon={<PaymentIcon />} label="P" size="small" color="success" />
                              </Tooltip>
                            )}
                            {!user.has_vendor && !user.has_booth && !user.has_payment && (
                              <Chip label="No data" size="small" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.business_name || '-'}
                        </TableCell>
                        <TableCell>
                          {user.booth_number || '-'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete this user">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteUser(user.user_id, user.actual_email)}
                              disabled={processing}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      })}

      {/* Merge Confirmation Dialog */}
      <Dialog
        open={!!mergeDialog}
        onClose={() => !processing && setMergeDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Merge Duplicate Accounts for {mergeDialog?.email}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Choose which account to keep. All vendor data, booth assignments, and payments 
            from other accounts will be transferred to the selected account.
          </Typography>
          
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Select account to keep:</FormLabel>
            <RadioGroup
              value={selectedKeepUser}
              onChange={(e) => setSelectedKeepUser(e.target.value)}
            >
              {mergeDialog?.users
                ?.reduce((acc, user) => {
                  const existing = acc.find(u => u.user_id === user.user_id);
                  if (!existing) {
                    acc.push(user);
                  }
                  return acc;
                }, [])
                .sort((a, b) => getUserPriority(b) - getUserPriority(a) || new Date(a.user_created) - new Date(b.user_created))
                .map(user => (
                <FormControlLabel
                  key={user.user_id}
                  value={user.user_id.toString()}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        <strong>User {user.user_id}</strong> - {user.actual_email}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Created: {formatDate(user.user_created)} | 
                        Data: {getDataSummary(user)} |
                        Business: {user.business_name || 'None'}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialog(null)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleMergeConfirm}
            variant="contained"
            disabled={!selectedKeepUser || processing}
            startIcon={processing ? <CircularProgress size={20} /> : <MergeIcon />}
          >
            {processing ? 'Merging...' : 'Merge Accounts'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateUserManager;
